/** Shared appearance presets for the admin panel (per-user prefs). */

/**
 * Each theme: [primary, soft, surface, text] for the Appearance swatch strip.
 * `scheme` is "light" | "dark" — applied as data-scheme so Tailwind dark: works for all dark themes.
 * Surface matches CSS: light = mix(white 70%, primary-soft); dark = mix(primary 14%, #111827).
 */
export const UI_THEME_OPTIONS = [
  // Light — [primary, soft, surface, text]
  { value: "violet", label: "Violet", scheme: "light", colors: ["#6366f1", "#e0e7ff", "#f6f8ff", "#0f172a"] },
  { value: "blue", label: "Blue", scheme: "light", colors: ["#2563eb", "#dbeafe", "#f4f9ff", "#0f172a"] },
  { value: "green", label: "Green", scheme: "light", colors: ["#059669", "#d1fae5", "#f1fef7", "#0f172a"] },
  { value: "amber", label: "Amber", scheme: "light", colors: ["#d97706", "#fef3c7", "#fffbee", "#0f172a"] },
  { value: "red", label: "Red", scheme: "light", colors: ["#e11d48", "#ffe4e6", "#fff7f8", "#0f172a"] },
  { value: "teal", label: "Teal", scheme: "light", colors: ["#0d9488", "#ccfbf1", "#f0fefb", "#0f172a"] },
  { value: "rose", label: "Rose", scheme: "light", colors: ["#e11d48", "#ffe4e6", "#fff7f8", "#0f172a"] },
  { value: "orange", label: "Orange", scheme: "light", colors: ["#ea580c", "#ffedd5", "#fffaf2", "#0f172a"] },
  { value: "indigo", label: "Indigo", scheme: "light", colors: ["#4f46e5", "#e0e7ff", "#f6f8ff", "#0f172a"] },
  { value: "slate", label: "Slate", scheme: "light", colors: ["#475569", "#e2e8f0", "#f6f8fa", "#0f172a"] },
  // Dark — [primary, soft mix, surface, text]
  { value: "midnight", label: "Midnight", scheme: "dark", colors: ["#818cf8", "#312e81", "#212844", "#f9fafb"] },
  { value: "dark-blue", label: "Ocean", scheme: "dark", colors: ["#60a5fa", "#1e3a5f", "#1c2c45", "#f9fafb"] },
  { value: "dark-green", label: "Forest", scheme: "dark", colors: ["#34d399", "#064e3b", "#163237", "#f9fafb"] },
  { value: "dark-amber", label: "Ember", scheme: "dark", colors: ["#fbbf24", "#78350f", "#322f27", "#f9fafb"] },
  { value: "dark-rose", label: "Dusk", scheme: "dark", colors: ["#fb7185", "#881337", "#322434", "#f9fafb"] },
  { value: "dark-slate", label: "Graphite", scheme: "dark", colors: ["#94a3b8", "#334155", "#232b3b", "#f9fafb"] },
];

export const LIGHT_THEME_OPTIONS = UI_THEME_OPTIONS.filter((t) => t.scheme === "light");
export const DARK_THEME_OPTIONS = UI_THEME_OPTIONS.filter((t) => t.scheme === "dark");

export const UI_FONT_OPTIONS = [
  {
    value: "inter",
    label: "Inter",
    family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  {
    value: "source-sans",
    label: "Source Sans 3",
    family: "'Source Sans 3', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: "ibm-plex",
    label: "IBM Plex Sans",
    family: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: "nunito-sans",
    label: "Nunito Sans",
    family: "'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: "dm-sans",
    label: "DM Sans",
    family: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: "plus-jakarta",
    label: "Plus Jakarta Sans",
    family: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: "manrope",
    label: "Manrope",
    family: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: "outfit",
    label: "Outfit",
    family: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: "figtree",
    label: "Figtree",
    family: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: "work-sans",
    label: "Work Sans",
    family: "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: "rubik",
    label: "Rubik",
    family: "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: "open-sans",
    label: "Open Sans",
    family: "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  {
    value: "lato",
    label: "Lato",
    family: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
];

export const DEFAULT_UI_THEME = "violet";
export const DEFAULT_UI_FONT = "inter";

const THEME_MAP = Object.fromEntries(UI_THEME_OPTIONS.map((opt) => [opt.value, opt]));
const FONT_MAP = Object.fromEntries(
  UI_FONT_OPTIONS.map((opt) => [opt.value, opt.family])
);

export function fontFamilyFor(key) {
  return FONT_MAP[key] || FONT_MAP[DEFAULT_UI_FONT];
}

export function schemeFor(theme) {
  return THEME_MAP[theme]?.scheme || "light";
}

export function applyAppearance(theme, font) {
  const root = document.documentElement;
  const resolved = theme || DEFAULT_UI_THEME;
  root.setAttribute("data-theme", resolved);
  root.setAttribute("data-scheme", schemeFor(resolved));
  root.style.setProperty("--font-sans", fontFamilyFor(font || DEFAULT_UI_FONT));
}
